/* eslint-disable max-len */
import React from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import uniq from "lodash/uniq";
import { useSelector } from "react-redux";
import { ResponsiveHeroImage, ResponsiveImage, Link } from "@quintype/components";
import { removeHtmlTags, replaceWithAlternates } from "../../utils";
import { isNonEmptyNode } from "../../utils/story";
import { useDeviceType } from "../../custom-hooks/useDeviceType";
import { StoryTemplateIcon } from "../StoryTemplateIcon";
import "./hero-image.m.css";

const getPadding = (aspectRatio) => {
  const [height, width] = aspectRatio;
  if (height > 0 && width > 0) {
    const padding = (width / height) * 100;
    return padding;
  }
  return 0;
};

export const HeroImage = ({
  story,
  aspectRatio,
  defaultWidth,
  widths,
  iconSizes,
  isStoryPageImage,
  isImageResponsive,
  sizes,
}) => {
  const fallbackImageUrl = "https://images.assettype.com/gulfnews-uat/2024-10-15/r2sl2lqd/Gulf-News-Fallback-Image.jpg";

  const alternateStory = story && replaceWithAlternates(story);

  const showImagePlaceholder = useSelector((state) => get(state, ["qt", "config", "showPlaceholder"], false));
  const getPlaceholderStyleName = showImagePlaceholder ? "placeholder" : "";
  const [mobileAspectRatio, desktopAspectRatio = mobileAspectRatio] = aspectRatio;
  const device = useDeviceType();
  const isMobile = device === "mobile";

  const imageAspectRatio = isMobile ? mobileAspectRatio : desktopAspectRatio;
  const imagePadding = isMobile ? getPadding(mobileAspectRatio) : getPadding(desktopAspectRatio);
  const slug =
    get(story, ["hero-image-s3-key"]) ||
    get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"]);
  const imageCaption = get(story, ["hero-image-caption"], "");
  const imageAltText = imageCaption ? removeHtmlTags(imageCaption) : get(story, ["headline"], "");
  const { "hero-image-caption": heroImageCaption = "", "hero-image-attribution": heroImageAttribution = "" } =
    story || {};
  const heroImageSlug = get(story, ["hero-image-s3-key"], "");
  const heroImageHyperlink = get(story, ["hero-image-hyperlink"], "");

  const getHeroImage = () => {
    if (!slug && fallbackImageUrl) {
      return (
        <figure
          styleName={`image ${getPlaceholderStyleName} fallback-image`}
          style={{
            paddingTop: imagePadding + `%`,
          }}
        >
          <img className="qt-image" src={`${fallbackImageUrl}`} alt="image-fallback"></img>
        </figure>
      );
    }
    return (
      <figure
        styleName={`image ${getPlaceholderStyleName}`}
        style={isImageResponsive ? { paddingTop: imagePadding + `%` } : { paddingTop: 0 }}
      >
        {slug && <StoryTemplateIcon storyTemplate={story["story-template"]} iconSizes={iconSizes} />}
        <ResponsiveHeroImage
          story={alternateStory}
          aspectRatio={imageAspectRatio}
          defaultWidth={defaultWidth}
          widths={uniq([...widths, 250, 480, 640])}
          imgParams={{ auto: ["format", "compress"], fit: "max" }}
          alt={imageAltText}
        />
      </figure>
    );
  };

  const getStoryHeroImage = () => {
    const ImageComponent = <ResponsiveImage
      slug={heroImageSlug}
      aspectRatio={imageAspectRatio}
      metadata={story?.["hero-image-metadata"]}
      sizes={sizes}
      defaultWidth={defaultWidth}
      widths={uniq([...widths, 250, 480, 640])}
      imgParams={{ auto: ["format", "compress"], fit: "max" }}
      alt={imageAltText}
      priority={true}
    />
    return (
      <figure
        styleName={`image ${getPlaceholderStyleName}`}
        style={isImageResponsive ? { paddingTop: imagePadding + `%` } : { paddingTop: 0 }}
      >
        {heroImageHyperlink ? (
          <Link href={heroImageHyperlink} target="_blank">
            {ImageComponent}
          </Link>
        ) : (
          ImageComponent
        )}
      </figure>
    );
  };
  return story ? (
    isStoryPageImage ? (
      heroImageSlug ? (
        <div styleName="hero-image-div">
          {getStoryHeroImage()}
          {(isNonEmptyNode(heroImageCaption) || isNonEmptyNode(heroImageAttribution)) && (
            <div styleName="hero-image-caption-attr">
              {isNonEmptyNode(heroImageCaption) && (
                <figcaption styleName="hero-image-caption" dangerouslySetInnerHTML={{ __html: heroImageCaption }} />
              )}

              {isNonEmptyNode(heroImageAttribution) && (
                <figcaption styleName="hero-image-credit" dangerouslySetInnerHTML={{ __html: heroImageAttribution }} />
              )}
            </div>
          )}
        </div>
      ) : null
    ) : (
      <div styleName="hero-image">{getHeroImage()}</div>
    )
  ) : null;
};

HeroImage.propTypes = {
  story: PropTypes.object.isRequired,
  aspectRatio: PropTypes.array,
  defaultWidth: PropTypes.number,
  widths: PropTypes.array,
  iconSizes: PropTypes.object,
  isImageResponsive: PropTypes.bool,
};

HeroImage.defaultProps = {
  aspectRatio: [
    [4, 3],
    [16, 9],
  ],
  defaultWidth: 480,
  widths: [250, 480, 640],
  iconSizes: { height: 24, width: 24 },
  isStoryPageImage: false,
  isImageResponsive: true,
  sizes: "(max-width: 768px) 90vw, 75vw",
};
